// CTurnIndicatorHand — 回合指示手勢（暫時透明）
// 目前保留既有物件生命週期與「戳」動畫，但 alpha 固定為 0；
// 實際回合提示改由 CSeatBadges 的 active 風字呈現，方便比較更清爽的牌桌視覺。
//
// 對應需求（手勢擺放方向 / frame / 動畫）：
// - 本家（下，seat 0）：手在「東」正上方，frame 0（拳頭朝下）→ 放大縮小
// - 下家（右，seat 1）：手在「南」左方，  frame 1（食指朝右）→ 左右移動
// - 對家（上，seat 2）：手在「西」下方，  frame 2（食指朝上）→ 上下移動
// - 上家（左，seat 3）：手在「北」右方，  frame 3（食指朝右）→ 左右移動
// 動畫 curve：戳出去（放大 / 移出）用 easeIn → 慢到快、不過衝；yoyo 回復 + repeatDelay
//       → 「戳一下、停、再戳」的節奏，無限 loop。
//
// 手勢相對「風字徽章中心」定位，徽章座標共用 seat-badges 的 SEAT_BADGE_SPOTS。
// frame index 依「相對玩家的座位方向」算（(seat - playerSeat + 4) % 4），playerSeat 換位也正確。
//
// === 使用圖片 ===
// - public/assets/ui/direction-hand.png（key: DIRECTION_HAND_KEY，2×2 spritesheet，每格 144×126）
//     原圖 asset/reference/direction_animate.png 直接複製，未放大

import type Phaser from 'phaser';

import { seatToRelative } from '@core/types';
import type { TSeat } from '@core/types';
import { DIRECTION_HAND_KEY } from '@scenes/boot-scene';

import { SEAT_BADGE_SPOTS } from './seat-badges';
import { TURN_INDICATOR_HAND_ALPHA } from './turn-indicator-presentation';
import { UIDepth } from './ui-depths';

// ---- 版面 / 動畫常數 ----
const HAND_SCALE = 0.8; // 144×126 frame → 顯示約 137×120 px
const HAND_DEPTH = UIDepth.TurnHand; // 高於牌面（預設 0），明顯浮在桌上
const POKE_MOVE_DISTANCE = 40; // 移動型「戳」的位移量（post-scale px）
const POKE_SCALE_PEAK = 1.18; // 本家放大縮小「戳」的放大峰值（× HAND_SCALE）
const POKE_DURATION_MS = 400; // 戳出去單程時長
const POKE_REPEAT_DELAY_MS = 100; // 每次戳之間的停頓（戳→停→戳的節奏）
const POKE_EASE = 'Quart.easeIn'; // 戳出去用 easeIn → 慢到快、不過衝
const FADE_OUT_MS = 200; // 淡出時長（玩家選好牌後手勢淡出消失）
const FADE_IN_MS = 200; // 淡入時長（本家摸牌整理結束後手勢淡入出現）

// 每家：手勢相對徽章的擺放偏移 + 動畫軸 + 移動型的戳方向（食指指向）
// 單一物件結構內的方向欄位，直接寫值 + 註解（同 SEAT_LAYOUT 風格，不再二次抽常數）
const HAND_DIR: Record<
  TSeat,
  {
    offset: { x: number; y: number }; // 相對徽章中心的擺放偏移（已含 HAND_GAP）
    axis: 'scale' | 'x' | 'y'; // 動畫軸（本家放大縮小、其他移動）
    pokeSign: number; // 移動型戳的方向（+1 / -1；scale 型忽略）
  }
> = {
  0: { offset: { x: 0, y: -70 }, axis: 'scale', pokeSign: 0 }, // 本家：東上方、放大縮小
  1: { offset: { x: -120, y: 0 }, axis: 'x', pokeSign: +1 }, // 下家：南左方、左右（食指朝右→戳右）
  2: { offset: { x: 0, y: 120 }, axis: 'y', pokeSign: -1 }, // 對家：西下方、上下（食指朝上→戳上）
  3: { offset: { x: 90, y: 0 }, axis: 'x', pokeSign: +1 }, // 上家：北右方、左右（食指朝右→戳右）
};

/**
 * 回合指示手勢。同一時間最多顯示一隻手（輪到誰就顯示誰）。
 * show() 換家時會先清掉舊的再建立；hide() 完全移除。
 */
export class CTurnIndicatorHand {
  private sprite: Phaser.GameObjects.Image | null = null;
  private currentSeat: TSeat | null = null;

  constructor(private readonly scene: Phaser.Scene) {}

  /**
   * 在 seat 的風字旁顯示手勢並開始戳動畫。
   * @param seat 輪到出牌的座位
   * @param playerSeat 玩家自己的座位（決定 frame 的相對方向）
   * @param options.fadeIn true 時手勢由透明淡入（本家摸牌整理結束後用）；不帶＝立即出現
   */
  show(seat: TSeat, playerSeat: TSeat, options?: { readonly fadeIn?: boolean }): void {
    if (this.currentSeat === seat) return; // 已在顯示同一家 → 不重啟動畫，避免抖動
    this.hide();

    const dir = HAND_DIR[seat];
    const badge = SEAT_BADGE_SPOTS[seat];
    const x = badge.x + dir.offset.x;
    const y = badge.y + dir.offset.y;
    const frame = seatToRelative(seat, playerSeat);

    const sprite = this.scene.add
      .image(x, y, DIRECTION_HAND_KEY, frame)
      .setScale(HAND_SCALE)
      .setDepth(HAND_DEPTH)
      .setAlpha(TURN_INDICATOR_HAND_ALPHA);
    this.sprite = sprite;
    this.currentSeat = seat;

    // 淡入（與戳動畫並行，alpha 軸獨立不衝突）
    if (options?.fadeIn) {
      sprite.setAlpha(0);
      this.scene.tweens.add({
        targets: sprite,
        alpha: TURN_INDICATOR_HAND_ALPHA,
        duration: FADE_IN_MS,
        ease: 'Linear',
      });
    }

    // 戳出去用 easeIn（慢到快、不過衝）、yoyo 回復、停頓後再戳
    const common = {
      targets: sprite,
      duration: POKE_DURATION_MS,
      ease: POKE_EASE,
      yoyo: true,
      repeat: -1,
      repeatDelay: POKE_REPEAT_DELAY_MS,
    } as const;

    if (dir.axis === 'scale') {
      this.scene.tweens.add({ ...common, scale: HAND_SCALE * POKE_SCALE_PEAK }); // 本家：放大縮小
    } else if (dir.axis === 'x') {
      this.scene.tweens.add({ ...common, x: x + dir.pokeSign * POKE_MOVE_DISTANCE }); // 左右移動
    } else {
      this.scene.tweens.add({ ...common, y: y + dir.pokeSign * POKE_MOVE_DISTANCE }); // 上下移動
    }
  }

  /** 移除手勢與其動畫。 */
  hide(): void {
    if (this.sprite) {
      this.scene.tweens.killTweensOf(this.sprite);
      this.sprite.destroy();
      this.sprite = null;
    }
    this.currentSeat = null;
  }

  /**
   * 淡出手勢後移除（玩家選好牌 → 手勢淡出消失，再開始出牌動畫）。
   * 立即把 sprite / currentSeat 視為「已不在顯示」，淡出的那張交由本地 ref tween 收尾，
   * 期間若 show() 重新顯示也不會互相干擾。
   */
  fadeOut(durationMs: number = FADE_OUT_MS): void {
    const sprite = this.sprite;
    this.sprite = null;
    this.currentSeat = null;
    if (!sprite) return;
    this.scene.tweens.killTweensOf(sprite);
    this.scene.tweens.add({
      targets: sprite,
      alpha: 0,
      duration: durationMs,
      ease: 'Linear',
      onComplete: () => sprite.destroy(),
    });
  }
}
